'use client';

import { c as _c } from "react/compiler-runtime";
import type { Cart, CartLine } from '@repo/shopify';
import { getTrackingValues } from '@shopify/hydrogen-react';
import { createContext, type Dispatch, type ReactNode, startTransition, useCallback, useContext, useEffect, useMemo, useOptimistic, useReducer, useRef, useState, useSyncExternalStore } from 'react';
import { applyOptimisticLineChange } from '@/components/cart/optimistic-line-changes';
import { isSubscriptionGiftLine } from '@/components/cart/reward-line-utils';
import { buildAddToCartEventFromItems, buildAnalyticsItemFromCartLine, buildRemoveFromCartEventFromItems, identifyKlaviyoProfile, identifyPostHogProfile, trackAnalyticsEvent } from '@/lib/analytics/client';
import { publishShopifyCartChange } from '@/lib/analytics/shopify-cart-events';
import type { PackageProtectionConfig } from '@/lib/cart-settings';
import { MARKET_COUNTRY_HEADER, MARKET_LANGUAGE_HEADER } from '@/lib/markets';
import { addCartLinesWithSelectedTransport, getBrowserCartAddContext } from '@/lib/shopify/browser-cart-add';
import { getCartLinesQuantity, getCartLinesSubtotal, splitPackageProtectionLines } from './package-protection-cart-utils';
import { getSelectedPackageProtectionVariant } from './use-package-protection';

// ============================================
// Types
// ============================================

interface CartState {
  cart: Cart | null;
  isOpen: boolean;
  isLoading: boolean;
  // Line IDs with a mutation (quantity change / removal) in flight. Quantity and
  // removal are optimistic (see `useOptimistic` in the provider); the UI dims
  // these lines and holds repricing controls (frequency, kit contents, reward
  // offers) until the server-confirmed cart arrives.
  pendingLineIds: ReadonlySet<string>;
}
interface AddItemOptions {
  openCart?: boolean;
  analyticsSource?: string;
  /** Attach the added line to a selling plan (Subscribe & Save). */
  sellingPlan?: {
    id: string;
    name: string;
  };
  /**
   * Companion lines added in the same cart mutation (e.g. free subscription
   * gifts). Never attached to the selling plan; line attributes come from
   * each line's `productInfo.attributes`.
   */
  extraLines?: {
    merchandiseId: string;
    quantity?: number;
    productInfo?: OptimisticProductInfo;
  }[];
}
export interface OptimisticProductInfo {
  productId?: string;
  title: string;
  handle: string;
  vendor?: string;
  productType?: string;
  image?: {
    url: string;
    altText?: string | null;
    width?: number;
    height?: number;
  } | null;
  variantTitle?: string;
  sku?: string | null;
  price: {
    amount: string;
    currencyCode: string;
  };
  attributes?: {
    key: string;
    value: string;
  }[];
  isRewardPending?: boolean;
  optimisticLineKey?: string;
}
export interface RewardClaimLineInput {
  merchandiseId: string;
  quantity: number;
  attributes?: {
    key: string;
    value: string;
  }[];
  /** Recurring selling plan for this line (e.g. hydrate bundle subscriptions). */
  sellingPlan?: {
    id: string;
    name: string;
  };
  productInfo: OptimisticProductInfo;
}
type CartAction = {
  type: 'SET_CART';
  payload: Cart | null;
} | {
  type: 'OPEN_CART';
} | {
  type: 'CLOSE_CART';
} | {
  type: 'SET_LOADING';
  payload: boolean;
} | {
  type: 'OPTIMISTIC_ADD';
  payload: {
    variantId: string;
    quantity: number;
    productInfo?: OptimisticProductInfo;
    sellingPlan?: {
      id: string;
      name: string;
    };
    openCart?: boolean;
  };
} | {
  type: 'SET_PENDING_LINE';
  payload: {
    lineId: string;
    pending: boolean;
  };
};
interface CartFetchResult {
  hasResponse: boolean;
  cart: Cart | null;
}
interface CartMutationResponse {
  cart?: Cart | null;
  error?: string;
  details?: string;
}
interface CartContextType extends CartState {
  addItem: (variantId: string, quantity?: number, productInfo?: OptimisticProductInfo, options?: AddItemOptions) => Promise<Cart | null>;
  claimRewardItems: (lines: RewardClaimLineInput[], options?: AddItemOptions) => Promise<Cart | null>;
  updateItem: (lineId: string, quantity: number) => Promise<void>;
  /** Upgrade a one-time line to a subscription by attaching a selling plan. */
  convertLineToSubscription: (lineId: string, quantity: number, sellingPlanId: string) => Promise<void>;
  /**
   * Rewrite a line's attributes and/or selling plan in one mutation (the
   * in-cart Crew Pack editor). `attributes` REPLACES the line's full set —
   * pass every attribute the line should keep.
   */
  updateLineOptions: (lineId: string, options: {
    quantity: number;
    sellingPlanId?: string;
    attributes?: {
      key: string;
      value: string;
    }[];
  }) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  updateBuyerIdentity: (buyerIdentity: {
    email?: string;
    phone?: string;
    countryCode?: string;
  }) => Promise<void>;
  loadCart: () => Promise<Cart | null>;
  hydrateCart: (cart: Cart | null) => void;
  suspendPackageProtectionSync: (durationMs?: number) => void;
  pendingLineIds: ReadonlySet<string>;
}
interface CartSummaryContextType {
  quantity: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
}

// ============================================
// Optimistic Helpers
// ============================================

function toImageOrNull(image: OptimisticProductInfo['image']) {
  if (!image) return null;
  return {
    url: image.url,
    altText: image.altText ?? null,
    width: image.width ?? 0,
    height: image.height ?? 0
  };
}
const EMPTY_PAGE_INFO = {
  hasNextPage: false,
  hasPreviousPage: false
};
const OPTIMISTIC_UPDATED_AT = new Date(0).toISOString();
/**
 * Retired first-order subscription free-shipping code. The Shopify discount is
 * disabled, so any cart still carrying it (persisted from before the change)
 * would show an inapplicable code at checkout — strip it. Safe to delete once
 * old carts have aged out.
 */
const LEGACY_SUBSCRIPTION_FREE_SHIPPING_CODE = 'SUBSHIP-FIRST';
const SHOPIFY_UNIQUE_TOKEN_HEADER = 'X-Shopify-UniqueToken';
const SHOPIFY_VISIT_TOKEN_HEADER = 'X-Shopify-VisitToken';
function normalizeLineAttributes(attributes: {
  key: string;
  value: string;
}[] | undefined) {
  return [...(attributes ?? [])].sort((left, right) => left.key === right.key ? left.value.localeCompare(right.value) : left.key.localeCompare(right.key)).map(attribute => `${attribute.key}:${attribute.value}`).join('|');
}
function cartLineIdentity(line: CartLine) {
  const sellingPlanId = line.sellingPlanAllocation?.sellingPlan.id ?? '';
  return `${line.merchandise.id}::${sellingPlanId}::${normalizeLineAttributes(line.attributes)}`;
}

/**
 * Free-gift line ids that must leave with `lineId`: when it's the cart's LAST
 * subscription line, the `_free_gift` lines ride along in the same remove
 * mutation so gifts never linger for a follow-up cleanup pass.
 */
function getCompanionGiftLineIds(cart: Cart | null, lineId: string, pendingIds: Set<string>, giftProductHandles: ReadonlySet<string>) {
  const lines = cart?.lines.edges.map(edge => edge.node) ?? [];
  const target = lines.find(line => line.id === lineId);
  const removesLastSubscription = !!target?.sellingPlanAllocation && !lines.some(line => line.id !== lineId && line.sellingPlanAllocation);
  if (!removesLastSubscription) return [];
  return lines.filter(line => line.id !== lineId && isSubscriptionGiftLine(line, giftProductHandles) && !line.id.startsWith('optimistic-') && !pendingIds.has(line.id)).map(line => line.id);
}
function buildPlaceholderLine(variantId: string, quantity: number, productInfo?: OptimisticProductInfo, sellingPlan?: {
  id: string;
  name: string;
}): {
  node: CartLine;
  cursor: string;
} {
  const priceAmount = productInfo?.price.amount ?? '0';
  const currencyCode = productInfo?.price.currencyCode ?? 'USD';
  const img = toImageOrNull(productInfo?.image);
  const optimisticLineKey = productInfo?.optimisticLineKey ?? (sellingPlan ? `${variantId}::${sellingPlan.id}` : variantId);
  return {
    cursor: `optimistic-${optimisticLineKey}`,
    node: {
      id: `optimistic-${optimisticLineKey}`,
      quantity,
      cost: {
        totalAmount: {
          amount: String(Number.parseFloat(priceAmount) * quantity),
          currencyCode
        },
        amountPerQuantity: {
          amount: priceAmount,
          currencyCode
        }
      },
      discountAllocations: [],
      attributes: productInfo?.attributes ?? [],
      sellingPlanAllocation: sellingPlan ? {
        sellingPlan
      } : null,
      isRewardPending: productInfo?.isRewardPending,
      merchandise: {
        id: variantId,
        title: productInfo?.variantTitle ?? 'Default Title',
        selectedOptions: [],
        product: {
          id: productInfo?.productId ?? '',
          handle: productInfo?.handle ?? '',
          title: productInfo?.title ?? 'Adding to cart...',
          vendor: productInfo?.vendor ?? '',
          productType: productInfo?.productType ?? '',
          featuredImage: img
        },
        price: {
          amount: priceAmount,
          currencyCode
        },
        image: img
      }
    }
  };
}
async function fetchCartSnapshot(buildCartRequestHeaders: (headersInit?: HeadersInit) => Headers, persistCartId: (cartId: string | null | undefined) => void): Promise<CartFetchResult> {
  try {
    const res = await fetch('/api/cart', {
      headers: buildCartRequestHeaders(),
      cache: 'no-store'
    });
    const data = (await res.json()) as {
      cart?: Cart | null;
    };
    if (data.cart?.id) {
      persistCartId(data.cart.id);
    }
    return {
      hasResponse: true,
      cart: data.cart ?? null
    };
  } catch {
    return {
      hasResponse: false,
      cart: null
    };
  }
}
async function readCartMutationResponse(response: Response, fallbackMessage: string) {
  const data = (await response.json()) as CartMutationResponse;
  if (!response.ok) {
    throw new Error(data.details ?? data.error ?? `${fallbackMessage} with ${response.status}`);
  }
  if (!data.cart) {
    throw new Error(data.error ?? `${fallbackMessage} without returning a cart.`);
  }
  return data.cart;
}
function dispatchOptimisticRewardLines(dispatch: Dispatch<CartAction>, lines: RewardClaimLineInput[], openCart: boolean | undefined) {
  for (const line of lines) {
    dispatch({
      type: 'OPTIMISTIC_ADD',
      payload: {
        variantId: line.merchandiseId,
        quantity: line.quantity,
        productInfo: line.productInfo,
        sellingPlan: line.sellingPlan,
        openCart
      }
    });
  }
}
function buildOptimisticCart(variantId: string, quantity: number, productInfo?: OptimisticProductInfo, sellingPlan?: {
  id: string;
  name: string;
}): Cart {
  const priceAmount = productInfo?.price.amount ?? '0';
  const currencyCode = productInfo?.price.currencyCode ?? 'USD';
  const totalAmount = String(Number.parseFloat(priceAmount) * quantity);
  return {
    id: 'optimistic-cart',
    updatedAt: OPTIMISTIC_UPDATED_AT,
    checkoutUrl: '/cart',
    totalQuantity: quantity,
    buyerIdentity: null,
    attributes: [],
    cost: {
      subtotalAmount: {
        amount: totalAmount,
        currencyCode
      },
      totalAmount: {
        amount: totalAmount,
        currencyCode
      },
      totalTaxAmount: null
    },
    lines: {
      edges: [buildPlaceholderLine(variantId, quantity, productInfo, sellingPlan)],
      pageInfo: EMPTY_PAGE_INFO
    }
  };
}
function addAmountToCost(cost: Cart['cost'], addedAmount: number): Cart['cost'] {
  return {
    ...cost,
    subtotalAmount: {
      ...cost.subtotalAmount,
      amount: String(Number.parseFloat(cost.subtotalAmount.amount) + addedAmount)
    },
    totalAmount: {
      ...cost.totalAmount,
      amount: String(Number.parseFloat(cost.totalAmount.amount) + addedAmount)
    }
  };
}
function optimisticAdd(cart: Cart | null, variantId: string, quantity: number, productInfo?: OptimisticProductInfo, sellingPlan?: {
  id: string;
  name: string;
}): Cart | null {
  if (!cart) {
    return buildOptimisticCart(variantId, quantity, productInfo, sellingPlan);
  }
  const targetAttributes = productInfo?.attributes;
  // A subscription line and a one-time line of the same variant are distinct
  // cart lines, so the merge target must match on selling plan too.
  const existingLine = productInfo?.optimisticLineKey ? undefined : cart.lines.edges.find(edge => edge.node.merchandise.id === variantId && (edge.node.sellingPlanAllocation?.sellingPlan.id ?? null) === (sellingPlan?.id ?? null) && normalizeLineAttributes(edge.node.attributes) === normalizeLineAttributes(targetAttributes));
  const newLines: typeof cart.lines = existingLine ? {
    ...cart.lines,
    edges: cart.lines.edges.map(edge => edge === existingLine ? {
      ...edge,
      node: {
        ...edge.node,
        quantity: edge.node.quantity + quantity,
        cost: {
          ...edge.node.cost,
          totalAmount: {
            ...edge.node.cost.totalAmount,
            amount: String(Number.parseFloat(edge.node.cost.amountPerQuantity.amount) * (edge.node.quantity + quantity))
          }
        }
      }
    } : edge)
  } : {
    ...cart.lines,
    edges: [...cart.lines.edges, buildPlaceholderLine(variantId, quantity, productInfo, sellingPlan)]
  };
  const addedAmount = existingLine ? Number.parseFloat(existingLine.node.cost.amountPerQuantity.amount) * quantity : Number.parseFloat(productInfo?.price.amount ?? '0') * quantity;
  return {
    ...cart,
    totalQuantity: cart.totalQuantity + quantity,
    lines: newLines,
    cost: addAmountToCost(cart.cost, addedAmount)
  };
}

// ============================================
// Reducer
// ============================================

type CartLineEdge = Cart['lines']['edges'][number];

/**
 * Server edge to render in a current line's position: identity match first,
 * then a same-line-ID fallback for lines whose identity changed in place —
 * e.g. a one-time line upgraded to a subscription keeps its cart line ID and
 * should keep its position rather than reappear at the bottom as "new".
 */
function takeMatchingServerEdge(currentEdge: CartLineEdge, serverEdgesByIdentity: Map<string, CartLineEdge[]>, serverEdgesById: Map<string, CartLineEdge>, usedLineIds: ReadonlySet<string>): CartLineEdge | undefined {
  const identityMatch = serverEdgesByIdentity.get(cartLineIdentity(currentEdge.node))?.shift();
  if (identityMatch) return identityMatch;
  const edgeById = serverEdgesById.get(currentEdge.node.id);
  if (!edgeById || usedLineIds.has(edgeById.node.id)) return undefined;

  // Claim it out of its identity bucket so a later identity match can't reuse it.
  const identityBucket = serverEdgesByIdentity.get(cartLineIdentity(edgeById.node));
  const bucketIndex = identityBucket?.indexOf(edgeById) ?? -1;
  if (identityBucket && bucketIndex >= 0) {
    identityBucket.splice(bucketIndex, 1);
  }
  return edgeById;
}

/**
 * When the server response arrives, preserve the line order from the current
 * (optimistic) state so items don't jump around. New lines from the server
 * are appended at the end.
 */
function preserveLineOrder(currentCart: Cart | null, serverCart: Cart): Cart {
  if (!currentCart) return serverCart;
  const serverEdgesByIdentity = new Map<string, typeof serverCart.lines.edges>();
  const serverEdgesById = new Map<string, (typeof serverCart.lines.edges)[number]>();
  for (const edge of serverCart.lines.edges) {
    const lineIdentity = cartLineIdentity(edge.node);
    const matchingEdges = serverEdgesByIdentity.get(lineIdentity);
    if (matchingEdges) {
      matchingEdges.push(edge);
    } else {
      serverEdgesByIdentity.set(lineIdentity, [edge]);
    }
    serverEdgesById.set(edge.node.id, edge);
  }

  // 1. Keep lines in the same order as the current cart, updating with server data
  const orderedEdges: typeof serverCart.lines.edges = [];
  const usedLineIds = new Set<string>();
  for (const currentEdge of currentCart.lines.edges) {
    const serverEdge = takeMatchingServerEdge(currentEdge, serverEdgesByIdentity, serverEdgesById, usedLineIds);
    if (serverEdge) {
      orderedEdges.push(serverEdge);
      usedLineIds.add(serverEdge.node.id);
    }
    // If server doesn't have this line, it was removed — skip it
  }

  // 2. Append any new lines from server that weren't in the optimistic cart
  for (const serverEdge of serverCart.lines.edges) {
    if (!usedLineIds.has(serverEdge.node.id)) {
      orderedEdges.push(serverEdge);
    }
  }
  return {
    ...serverCart,
    lines: {
      ...serverCart.lines,
      edges: orderedEdges
    }
  };
}
function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'SET_CART':
      return {
        ...state,
        cart: action.payload ? preserveLineOrder(state.cart, action.payload) : null,
        isLoading: false
      };
    case 'OPEN_CART':
      return {
        ...state,
        isOpen: true
      };
    case 'CLOSE_CART':
      return {
        ...state,
        isOpen: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };
    case 'OPTIMISTIC_ADD':
      return {
        ...state,
        cart: optimisticAdd(state.cart, action.payload.variantId, action.payload.quantity, action.payload.productInfo, action.payload.sellingPlan),
        isOpen: action.payload.openCart === false ? state.isOpen : true
      };
    case 'SET_PENDING_LINE':
      {
        const nextPendingLineIds = new Set(state.pendingLineIds);
        if (action.payload.pending) {
          nextPendingLineIds.add(action.payload.lineId);
        } else {
          nextPendingLineIds.delete(action.payload.lineId);
        }
        return {
          ...state,
          pendingLineIds: nextPendingLineIds
        };
      }
    default:
      return state;
  }
}

// ============================================
// Context
// ============================================

const CartContext = createContext<CartContextType | null>(null);
const CartSummaryContext = createContext<CartSummaryContextType | null>(null);
export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
export function useCartSummary() {
  const context = useContext(CartSummaryContext);
  if (!context) {
    throw new Error("useCartSummary must be used within a CartProvider");
  }
  return context;
}

// ============================================
// Cookie Helpers
// ============================================

function getCartIdFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)cartId=([^;]*)/);
  return match ? decodeCartCookieValue(match[1]) : null;
}
function getSecureCookieAttribute() {
  return typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
}
function setCartIdCookie(cartId: string) {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API lacks broad support; simple cookie set is sufficient here
  document.cookie = `cartId=${encodeURIComponent(decodeCartCookieValue(cartId))}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax${getSecureCookieAttribute()}`;
}
function clearCartIdCookie() {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API lacks broad support; simple cookie clear is sufficient here
  document.cookie = `cartId=; path=/; max-age=0; SameSite=Lax${getSecureCookieAttribute()}`;
}
function decodeCartCookieValue(value: string) {
  let decodedValue = value;
  for (let index = 0; index < 5; index += 1) {
    try {
      const nextValue = decodeURIComponent(decodedValue);
      if (nextValue === decodedValue) {
        break;
      }
      decodedValue = nextValue;
    } catch {
      break;
    }
  }
  return decodedValue;
}
function isPersistentCartId(cartId: string | null | undefined): cartId is string {
  return Boolean(cartId && cartId !== 'optimistic-cart');
}
function getCartQtyFromCookie(): number {
  if (typeof document === 'undefined') return 0;
  const match = document.cookie.match(/(?:^|;\s*)cartQty=([^;]*)/);
  return match ? Number.parseInt(decodeURIComponent(match[1]), 10) || 0 : 0;
}

// The `cartQty` cookie is an external store for the header badge: the static
// shell is shared by every visitor, so the badge's inline script paints the
// cookie value before first paint and `useSyncExternalStore` lets React
// hydrate against it without a 0 → N flicker (see site-header-controls.tsx).
const cartQtyListeners = new Set<() => void>();
function subscribeCartQtyCookie(listener: () => void) {
  cartQtyListeners.add(listener);
  return () => {
    cartQtyListeners.delete(listener);
  };
}
function getServerCartQtySnapshot() {
  return 0;
}
function setCartQtyCookie(qty: number) {
  // biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API lacks broad support; simple cookie set is sufficient here
  document.cookie = `cartQty=${qty}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax${getSecureCookieAttribute()}`;
  for (const listener of cartQtyListeners) listener();
}
function buildPackageProtectionSignature(pathname: string, lines: CartLine[], packageProtection: PackageProtectionConfig) {
  return [pathname, packageProtection.enabled ? 'enabled' : 'disabled', packageProtection.productHandle, ...lines.map(line => [line.id, line.merchandise.id, line.quantity, line.cost.totalAmount.amount].join(':'))].join('|');
}
function isTrackableCart(cart: Cart | null | undefined): cart is Cart {
  return Boolean(cart && cart.id !== 'optimistic-cart');
}
function mapCartLinesById(cart: Cart | null | undefined) {
  const lines = new Map<string, CartLine>();
  if (!isTrackableCart(cart)) {
    return lines;
  }
  for (const edge of cart.lines.edges) {
    lines.set(edge.node.id, edge.node);
  }
  return lines;
}
type CartAnalyticsItem = ReturnType<typeof buildAnalyticsItemFromCartLine>;
interface RequestedCartLineInput {
  merchandiseId: string;
  quantity: number;
}
interface PendingCartAnalytics {
  source: string;
  requestedLines?: RequestedCartLineInput[];
}

// Automatic background mutations (package-protection reconciliation/cleanup) are not
// user actions, so their cart changes must not be reported to ad vendors. The
// user-initiated protection add uses its own source and still tracks.
const SILENT_CART_ANALYTICS_SOURCES = new Set(['package_protection_cleanup', 'package_protection_reconcile']);

/**
 * Builds the added-items list for a mutation that has no tracked baseline cart
 * (e.g. the first mutation of a session on a returning visitor's saved cart).
 * Diffing against an empty baseline would report every pre-existing line as
 * freshly added, so only the lines the mutation actually requested are emitted,
 * with the requested delta quantities.
 */
function buildAddedItemsFromRequestedLines(cart: Cart, requestedLines: RequestedCartLineInput[] | undefined): CartAnalyticsItem[] {
  const addedItems: CartAnalyticsItem[] = [];
  if (!requestedLines || requestedLines.length === 0) {
    return addedItems;
  }
  const requestedQuantities = new Map<string, number>();
  for (const requestedLine of requestedLines) {
    requestedQuantities.set(requestedLine.merchandiseId, (requestedQuantities.get(requestedLine.merchandiseId) ?? 0) + requestedLine.quantity);
  }
  for (const edge of cart.lines.edges) {
    const requestedQuantity = requestedQuantities.get(edge.node.merchandise.id);
    if (!requestedQuantity) {
      continue;
    }
    requestedQuantities.delete(edge.node.merchandise.id);
    addedItems.push({
      ...buildAnalyticsItemFromCartLine(edge.node),
      quantity: Math.min(requestedQuantity, edge.node.quantity),
      currentQuantity: edge.node.quantity
    });
  }
  return addedItems;
}
function diffCartLinesForAnalytics(previousCart: Cart, cart: Cart) {
  const addedItems: CartAnalyticsItem[] = [];
  const removedItems: CartAnalyticsItem[] = [];
  const previousLines = mapCartLinesById(previousCart);
  const currentLines = mapCartLinesById(cart);
  for (const [lineId, previousLine] of previousLines) {
    const currentLine = currentLines.get(lineId);
    if (!currentLine) {
      removedItems.push({
        ...buildAnalyticsItemFromCartLine(previousLine),
        quantity: previousLine.quantity
      });
      continue;
    }
    if (previousLine.quantity < currentLine.quantity) {
      addedItems.push({
        ...buildAnalyticsItemFromCartLine(currentLine),
        quantity: currentLine.quantity - previousLine.quantity,
        currentQuantity: currentLine.quantity
      });
    } else if (previousLine.quantity > currentLine.quantity) {
      removedItems.push({
        ...buildAnalyticsItemFromCartLine(previousLine),
        quantity: previousLine.quantity - currentLine.quantity,
        currentQuantity: currentLine.quantity
      });
    }
  }
  for (const [lineId, currentLine] of currentLines) {
    if (previousLines.has(lineId)) {
      continue;
    }
    addedItems.push({
      ...buildAnalyticsItemFromCartLine(currentLine),
      quantity: currentLine.quantity,
      currentQuantity: currentLine.quantity
    });
  }
  return {
    addedItems,
    removedItems
  };
}
function buildCartMutationAnalyticsItems(previousCart: Cart | null, cart: Cart, requestedLines: RequestedCartLineInput[] | undefined): {
  addedItems: CartAnalyticsItem[];
  removedItems: CartAnalyticsItem[];
} {
  if (previousCart) {
    return diffCartLinesForAnalytics(previousCart, cart);
  }
  return {
    addedItems: buildAddedItemsFromRequestedLines(cart, requestedLines),
    removedItems: []
  };
}
function emitCartMutationAnalytics(cart: Cart, source: string, addedItems: CartAnalyticsItem[], removedItems: CartAnalyticsItem[]) {
  const cartItems = cart.lines.edges.map(edge => buildAnalyticsItemFromCartLine(edge.node));
  if (addedItems.length > 0) {
    trackAnalyticsEvent(buildAddToCartEventFromItems({
      items: addedItems,
      cartItems,
      cartId: cart.id,
      checkoutUrl: cart.checkoutUrl,
      source
    }));
  }
  if (removedItems.length > 0) {
    trackAnalyticsEvent(buildRemoveFromCartEventFromItems({
      items: removedItems,
      cartItems,
      cartId: cart.id,
      checkoutUrl: cart.checkoutUrl,
      source
    }));
  }
}
function createShopifyTrackingToken() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `shopify-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
function useBrowserPathname() {
  const $ = _c(2);
  const [pathname, setPathname] = useState("");
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      let isMounted = true;
      const updatePathname = () => {
        if (!isMounted) {
          return;
        }
        setPathname(_temp);
      };
      const schedulePathnameUpdate = () => {
        queueMicrotask(updatePathname);
      };
      updatePathname();
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;
      window.history.pushState = function pushState(...t2) {
        const args = t2;
        originalPushState.apply(window.history, args);
        schedulePathnameUpdate();
      };
      window.history.replaceState = function replaceState(...t3) {
        const args_0 = t3;
        originalReplaceState.apply(window.history, args_0);
        schedulePathnameUpdate();
      };
      window.addEventListener("popstate", updatePathname);
      return () => {
        isMounted = false;
        window.history.pushState = originalPushState;
        window.history.replaceState = originalReplaceState;
        window.removeEventListener("popstate", updatePathname);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return pathname;
}

// ============================================
// Provider
// ============================================
function _temp(currentPathname) {
  return currentPathname === window.location.pathname ? currentPathname : window.location.pathname;
}
export function CartProvider({
  children,
  packageProtection: configuredPackageProtection,
  market,
  subscriptionGiftHandles
}: {
  children: ReactNode;
  packageProtection: PackageProtectionConfig;
  market?: {
    country: string;
    language: string;
    countryCode: string;
    languageCode: string;
  };
  /** Gift product handles, for sweeping legacy gift lines without `_free_gift`. */
  subscriptionGiftHandles?: string[];
}) {
  const pathname = useBrowserPathname();
  const isUsMarket = market?.country ? market.country.toLowerCase() === 'us' : true;
  const giftProductHandles = useMemo(() => new Set((subscriptionGiftHandles ?? []).map(handle => handle.toLowerCase())), [subscriptionGiftHandles]);
  const packageProtection = useMemo(() => isUsMarket ? configuredPackageProtection : {
    ...configuredPackageProtection,
    enabled: false,
    variants: []
  }, [configuredPackageProtection, isUsMarket]);
  const [state, dispatch] = useReducer(cartReducer, {
    cart: null,
    isOpen: false,
    isLoading: false,
    pendingLineIds: new Set<string>()
  });
  // Optimistic quantity / removal layer over the server-confirmed cart. A change
  // is applied inside the transition that runs its mutation, so the drawer
  // updates on the click; when the transition settles React re-derives the
  // value from `state.cart` — the confirmed cart already carries the change, or
  // the recovery fetch restored the previous state and the line snaps back.
  const [optimisticCart, applyOptimisticChange] = useOptimistic(state.cart, applyOptimisticLineChange);
  // Latest requested quantity per line. Quantity taps stack optimistically;
  // the serialized mutation queue sends only the newest target per line so a
  // burst of +/+/+ becomes one or two requests instead of one per tap.
  const desiredLineQuantityRef = useRef(new Map<string, number>());
  const packageProtectionSyncSuspendedUntilRef = useRef(0);
  const packageProtectionSyncInFlightRef = useRef(false);
  const cartMutationInFlightRef = useRef(false);
  const cartMutationVersionRef = useRef(0);
  const cartLoadInFlightRef = useRef<Promise<CartFetchResult> | null>(null);
  const cartIdRef = useRef<string | null>(getCartIdFromCookie());
  // Synchronous mirror of state.pendingLineIds, used to guard against double-firing a
  // mutation on a line and to keep the package-protection sync from running mid-mutation.
  const pendingLineIdsRef = useRef(new Set<string>());
  // Render-synced mirror of state.cart so stable callbacks (removeItem) can
  // inspect the latest lines without depending on cart state.
  const cartStateRef = useRef<Cart | null>(null);
  cartStateRef.current = state.cart;
  // Serializes line update/remove mutations so concurrent requests on the same cart can't
  // race and return stale sibling lines. Each line still shows pending immediately.
  const lineMutationChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const shopifyTrackingTokensRef = useRef<{
    uniqueToken: string;
    visitToken: string;
  } | null>(null);
  const trackedCartRef = useRef<Cart | null>(null);
  const pendingCartAnalyticsRef = useRef<PendingCartAnalytics | null>(null);
  const lastPackageProtectionSignatureRef = useRef('');
  const buildCartRequestHeaders = useCallback((headersInit?: HeadersInit) => {
    const headers = new Headers(headersInit);
    const trackingValues = getTrackingValues();
    const uniqueToken = trackingValues.uniqueToken || shopifyTrackingTokensRef.current?.uniqueToken || createShopifyTrackingToken();
    const visitToken = trackingValues.visitToken || shopifyTrackingTokensRef.current?.visitToken || createShopifyTrackingToken();
    shopifyTrackingTokensRef.current = {
      uniqueToken,
      visitToken
    };
    headers.set(SHOPIFY_UNIQUE_TOKEN_HEADER, uniqueToken);
    headers.set(SHOPIFY_VISIT_TOKEN_HEADER, visitToken);
    if (market) {
      headers.set(MARKET_COUNTRY_HEADER, market.country);
      headers.set(MARKET_LANGUAGE_HEADER, market.language);
    }
    return headers;
  }, [market]);

  // Cookie quantity — shown until the real cart loads. Read through
  // `useSyncExternalStore` so hydration matches the server's `0` and React
  // re-renders to the cookie value synchronously before paint, which keeps
  // the badge the inline script already painted (see site-header-controls).
  const cachedQty = useSyncExternalStore(subscribeCartQtyCookie, getCartQtyFromCookie, getServerCartQtySnapshot);

  // Strip the retired SUBSHIP-FIRST code from carts that still carry it.
  const discountCodeSyncInFlightRef = useRef(false);
  useEffect(() => {
    const cart = state.cart;
    // Optimistic placeholder carts don't carry discountCodes — wait for the
    // server-confirmed cart before deciding anything.
    if (!cart?.discountCodes) return;
    if (discountCodeSyncInFlightRef.current || cartMutationInFlightRef.current) return;
    const codes = cart.discountCodes.map(entry => entry.code);
    const nextCodes = codes.filter(code => code.toUpperCase() !== LEGACY_SUBSCRIPTION_FREE_SHIPPING_CODE);
    if (nextCodes.length === codes.length) return;
    discountCodeSyncInFlightRef.current = true;
    const mutationVersion = ++cartMutationVersionRef.current;
    void fetch('/api/cart', {
      method: 'POST',
      headers: buildCartRequestHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        action: 'updateDiscountCodes',
        cartId: cart.id,
        discountCodes: nextCodes,
        source: 'subscription_free_shipping_sync'
      })
    }).then(async response => {
      const nextCart = await readCartMutationResponse(response, 'Cart discount sync failed');
      if (cartMutationVersionRef.current === mutationVersion) {
        dispatch({
          type: 'SET_CART',
          payload: nextCart
        });
      }
    }).catch(() => {
      // Shipping stays at the profile rate — never block the cart on this.
    }).finally(() => {
      discountCodeSyncInFlightRef.current = false;
    });
  }, [state.cart, buildCartRequestHeaders]);

  // Subscription gifts come WITH a subscription: when the last selling-plan
  // line leaves the cart, drop the `_free_gift` lines too so removing the
  // subscription can never keep its free gifts. Mirrors the discount-code
  // cleanup above — reacts only to server-confirmed carts.
  const giftCleanupInFlightRef = useRef(false);
  useEffect(() => {
    const cart_0 = state.cart;
    // Optimistic placeholder carts don't carry discountCodes — wait for the
    // server-confirmed cart before deciding anything.
    if (!cart_0?.discountCodes) return;
    if (giftCleanupInFlightRef.current || cartMutationInFlightRef.current) return;
    const lines = cart_0.lines.edges.map(edge => edge.node);
    if (lines.some(line => line.sellingPlanAllocation)) return;
    const giftLineIds = lines.filter(line_0 => isSubscriptionGiftLine(line_0, giftProductHandles)).map(line_1 => line_1.id);
    if (giftLineIds.length === 0) return;
    giftCleanupInFlightRef.current = true;
    const mutationVersion_0 = ++cartMutationVersionRef.current;
    void fetch('/api/cart', {
      method: 'POST',
      headers: buildCartRequestHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        action: 'remove',
        cartId: cart_0.id,
        lineIds: giftLineIds,
        source: 'subscription_gift_cleanup'
      })
    }).then(async response_0 => {
      const nextCart_0 = await readCartMutationResponse(response_0, 'Gift cleanup failed');
      if (cartMutationVersionRef.current === mutationVersion_0) {
        dispatch({
          type: 'SET_CART',
          payload: nextCart_0
        });
      }
    }).catch(() => {
      // Leave the gift lines; any later cart change retriggers the cleanup.
    }).finally(() => {
      giftCleanupInFlightRef.current = false;
    });
  }, [state.cart, buildCartRequestHeaders, giftProductHandles]);
  const setLinePending = useCallback((lineId: string, pending: boolean) => {
    if (pending) {
      pendingLineIdsRef.current.add(lineId);
    } else {
      pendingLineIdsRef.current.delete(lineId);
    }
    dispatch({
      type: 'SET_PENDING_LINE',
      payload: {
        lineId,
        pending
      }
    });
  }, []);
  const setLinesPending = useCallback((lineIds: string[], pending_0: boolean) => {
    for (const lineId_0 of lineIds) setLinePending(lineId_0, pending_0);
  }, [setLinePending]);
  const enqueueLineMutation = useCallback((task: () => Promise<void>) => {
    const run = lineMutationChainRef.current.then(task, task);
    // Keep the chain resolvable so one failed mutation doesn't wedge the queue.
    lineMutationChainRef.current = run.catch(() => undefined);
    return run;
  }, []);

  // Sync cart quantity to cookie whenever it changes
  useEffect(() => {
    if (!state.cart) return;
    const rawLines = state.cart?.lines.edges.map(edge_0 => edge_0.node) ?? [];
    const {
      visibleLines
    } = splitPackageProtectionLines(rawLines, packageProtection.productHandle);
    const qty = getCartLinesQuantity(visibleLines);
    setCartQtyCookie(qty);
  }, [state.cart, packageProtection.productHandle]);
  useEffect(function syncTrackedCartAnalytics() {
    const pendingAnalytics = pendingCartAnalyticsRef.current;
    const cart_1 = state.cart;
    if (!pendingAnalytics) {
      if (!cartMutationInFlightRef.current) {
        trackedCartRef.current = isTrackableCart(cart_1) ? cart_1 : null;
      }
      return;
    }
    pendingCartAnalyticsRef.current = null;
    if (!isTrackableCart(cart_1)) {
      trackedCartRef.current = null;
      return;
    }
    const previousCart = isTrackableCart(trackedCartRef.current) ? trackedCartRef.current : null;
    trackedCartRef.current = cart_1;

    // Background protection-line reconciliation isn't a user action: keep the
    // tracked baseline in sync but don't emit add/remove events for it.
    if (SILENT_CART_ANALYTICS_SOURCES.has(pendingAnalytics.source)) {
      return;
    }
    const {
      addedItems,
      removedItems
    } = buildCartMutationAnalyticsItems(previousCart, cart_1, pendingAnalytics.requestedLines);

    // An empty diff means the mutation didn't change tracked lines (or arrived as a
    // duplicate snapshot) — the diff is the dedupe, no timestamp guard needed.
    emitCartMutationAnalytics(cart_1, pendingAnalytics.source, addedItems, removedItems);
    publishShopifyCartChange({
      previousCart,
      cart: cart_1,
      requestedLines: pendingAnalytics.requestedLines
    });
  }, [state.cart]);
  const persistCartId = useCallback((cartId: string | null | undefined) => {
    if (!isPersistentCartId(cartId)) {
      return;
    }
    cartIdRef.current = cartId;
    setCartIdCookie(cartId);
  }, []);
  const getCurrentCartId = useCallback(() => {
    const stateCartId = state.cart?.id;
    if (isPersistentCartId(stateCartId)) {
      cartIdRef.current = stateCartId;
      return stateCartId;
    }
    if (isPersistentCartId(cartIdRef.current)) {
      return cartIdRef.current;
    }
    const cookieCartId = getCartIdFromCookie();
    if (isPersistentCartId(cookieCartId)) {
      cartIdRef.current = cookieCartId;
      return cookieCartId;
    }
    return null;
  }, [state.cart?.id]);
  const applyLoadedCart = useCallback((cart_2: Cart | null) => {
    if (cart_2?.id) {
      persistCartId(cart_2.id);
    } else {
      cartIdRef.current = null;
      clearCartIdCookie();
      setCartQtyCookie(0);
    }
    dispatch({
      type: 'SET_CART',
      payload: cart_2
    });
  }, [persistCartId]);
  const loadCartForUi = useCallback(async () => {
    const cartId_0 = getCurrentCartId();
    if (!cartId_0) {
      applyLoadedCart(null);
      return null;
    }
    if (state.cart?.id === cartId_0) {
      return state.cart;
    }
    if (cartLoadInFlightRef.current) {
      return (await cartLoadInFlightRef.current).cart;
    }
    dispatch({
      type: 'SET_LOADING',
      payload: true
    });
    const request = fetchCartSnapshot(buildCartRequestHeaders, persistCartId);
    cartLoadInFlightRef.current = request;
    try {
      const snapshot = await request;
      if (snapshot.hasResponse) {
        applyLoadedCart(snapshot.cart);
      }
      return snapshot.cart;
    } finally {
      cartLoadInFlightRef.current = null;
      dispatch({
        type: 'SET_LOADING',
        payload: false
      });
    }
  }, [applyLoadedCart, buildCartRequestHeaders, getCurrentCartId, persistCartId, state.cart]);
  const hydrateCart = useCallback((cart_3: Cart | null) => applyLoadedCart(cart_3), [applyLoadedCart]);
  const openCart = useCallback(() => {
    dispatch({
      type: 'OPEN_CART'
    });
    void loadCartForUi();
  }, [loadCartForUi]);
  const closeCart = useCallback(() => dispatch({
    type: 'CLOSE_CART'
  }), []);
  const suspendPackageProtectionSync = useCallback((durationMs = 10000) => {
    packageProtectionSyncSuspendedUntilRef.current = Date.now() + durationMs;
  }, []);
  const recoverCartAfterMutationFailure = useCallback(async () => {
    const cartId_1 = getCurrentCartId();
    if (!cartId_1) {
      dispatch({
        type: 'SET_CART',
        payload: null
      });
      return;
    }
    const snapshot = await fetchCartSnapshot(buildCartRequestHeaders, persistCartId);
    if (snapshot.hasResponse) {
      applyLoadedCart(snapshot.cart);
    }
  }, [applyLoadedCart, buildCartRequestHeaders, getCurrentCartId, persistCartId]);
  const addItem = useCallback(async function addItemImpl(variantId: string, quantity = 1, productInfo?: OptimisticProductInfo, options?: AddItemOptions) {
    if (cartMutationInFlightRef.current) return null;
    const mutationVersion_1 = ++cartMutationVersionRef.current;
    const extraLines = options?.extraLines ?? [];

    // Optimistically update UI immediately
    dispatch({
      type: 'OPTIMISTIC_ADD',
      payload: {
        variantId,
        quantity,
        productInfo,
        sellingPlan: options?.sellingPlan,
        openCart: options?.openCart
      }
    });
    for (const line_2 of extraLines) {
      dispatch({
        type: 'OPTIMISTIC_ADD',
        payload: {
          variantId: line_2.merchandiseId,
          quantity: line_2.quantity ?? 1,
          productInfo: line_2.productInfo,
          openCart: false
        }
      });
    }
    dispatch({
      type: 'SET_LOADING',
      payload: true
    });
    cartMutationInFlightRef.current = true;
    try {
      const cartId_2 = getCurrentCartId();
      const lines_0 = [{
        merchandiseId: variantId,
        quantity,
        sellingPlanId: options?.sellingPlan?.id,
        attributes: productInfo?.attributes
      }, ...extraLines.map(line_3 => ({
        merchandiseId: line_3.merchandiseId,
        quantity: line_3.quantity ?? 1,
        attributes: line_3.productInfo?.attributes
      }))];
      const cart_4 = await addCartLinesWithSelectedTransport({
        context: getBrowserCartAddContext({
          cartId: cartId_2,
          cartCountryCode: cartStateRef.current?.buyerIdentity?.countryCode,
          countryCode: market?.countryCode ?? 'US',
          languageCode: market?.languageCode ?? 'EN'
        }),
        lines: lines_0,
        serverAdd: async () => {
          const res = await fetch('/api/cart', {
            method: 'POST',
            headers: buildCartRequestHeaders({
              'Content-Type': 'application/json'
            }),
            body: JSON.stringify({
              action: 'add',
              cartId: cartId_2,
              source: options?.analyticsSource ?? 'cart_provider',
              lines: lines_0
            })
          });
          return readCartMutationResponse(res, 'Cart add failed');
        }
      });
      // Replace optimistic data with real server data
      persistCartId(cart_4.id);
      pendingCartAnalyticsRef.current = {
        source: options?.analyticsSource ?? 'cart_provider',
        requestedLines: [{
          merchandiseId: variantId,
          quantity
        }, ...extraLines.map(line_4 => ({
          merchandiseId: line_4.merchandiseId,
          quantity: line_4.quantity ?? 1
        }))]
      };
      if (cartMutationVersionRef.current === mutationVersion_1) {
        dispatch({
          type: 'SET_CART',
          payload: cart_4
        });
      }
      return cart_4;
    } catch (error) {
      console.error('Failed to add item to cart:', error);
      await recoverCartAfterMutationFailure();
    } finally {
      cartMutationInFlightRef.current = false;
      dispatch({
        type: 'SET_LOADING',
        payload: false
      });
    }
    return null;
  }, [buildCartRequestHeaders, getCurrentCartId, persistCartId, recoverCartAfterMutationFailure, market?.countryCode, market?.languageCode]);
  const claimRewardItems = useCallback(async (lines_1: RewardClaimLineInput[], options_0?: AddItemOptions) => {
    if (cartMutationInFlightRef.current || lines_1.length === 0) return null;
    const mutationVersion_2 = ++cartMutationVersionRef.current;
    const analyticsSource = options_0?.analyticsSource ?? 'free_tee_reward';
    dispatchOptimisticRewardLines(dispatch, lines_1, options_0?.openCart);
    dispatch({
      type: 'SET_LOADING',
      payload: true
    });
    cartMutationInFlightRef.current = true;
    try {
      const cartId_3 = getCurrentCartId();
      const res_0 = await fetch('/api/cart', {
        method: 'POST',
        headers: buildCartRequestHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          action: 'claimReward',
          cartId: cartId_3,
          source: analyticsSource,
          lines: lines_1.map(line_5 => ({
            merchandiseId: line_5.merchandiseId,
            quantity: line_5.quantity,
            attributes: line_5.attributes,
            sellingPlanId: line_5.sellingPlan?.id
          }))
        })
      });
      const cart_5 = await readCartMutationResponse(res_0, 'Reward claim failed');
      persistCartId(cart_5.id);
      if (options_0?.openCart !== false) {
        dispatch({
          type: 'OPEN_CART'
        });
      }
      pendingCartAnalyticsRef.current = {
        source: analyticsSource,
        requestedLines: lines_1.map(line_6 => ({
          merchandiseId: line_6.merchandiseId,
          quantity: line_6.quantity
        }))
      };
      if (cartMutationVersionRef.current === mutationVersion_2) {
        dispatch({
          type: 'SET_CART',
          payload: cart_5
        });
      }
      return cart_5;
    } catch (error_0) {
      console.error('Failed to claim reward items:', error_0);
      await recoverCartAfterMutationFailure();
    } finally {
      cartMutationInFlightRef.current = false;
      dispatch({
        type: 'SET_LOADING',
        payload: false
      });
    }
    return null;
  }, [buildCartRequestHeaders, getCurrentCartId, recoverCartAfterMutationFailure, persistCartId]);
  const updateItem = useCallback((lineId_1: string, quantity_0: number) => {
    if (quantity_0 < 1) return Promise.resolve();
    // Optimistic: the line shows the new quantity immediately and the
    // confirmed cart (discounts, reward thresholds) replaces it when the
    // serialized mutation lands. Taps stack — each one records the newest
    // target and the queue collapses a burst into the last value.
    desiredLineQuantityRef.current.set(lineId_1, quantity_0);
    setLinePending(lineId_1, true);
    const mutation = enqueueLineMutation(async () => {
      const target = desiredLineQuantityRef.current.get(lineId_1);
      // A later tap already flushed this line's newest quantity.
      if (target === undefined) return;
      desiredLineQuantityRef.current.delete(lineId_1);
      const cartId_4 = getCurrentCartId();
      if (!cartId_4) return;
      const mutationVersion_3 = ++cartMutationVersionRef.current;
      try {
        const res_1 = await fetch('/api/cart', {
          method: 'POST',
          headers: buildCartRequestHeaders({
            'Content-Type': 'application/json'
          }),
          body: JSON.stringify({
            action: 'update',
            cartId: cartId_4,
            source: 'cart_update',
            lines: [{
              id: lineId_1,
              quantity: target
            }]
          })
        });
        const data = await res_1.json();
        if (!data.cart) return;
        persistCartId(data.cart.id);
        pendingCartAnalyticsRef.current = {
          source: 'cart_update'
        };
        if (cartMutationVersionRef.current === mutationVersion_3) {
          dispatch({
            type: 'SET_CART',
            payload: data.cart
          });
        }
      } catch (error_1) {
        console.error('Failed to update cart item:', error_1);
        await recoverCartAfterMutationFailure();
      }
    });
    const settled = mutation.catch(() => undefined);
    void settled.then(() => {
      if (!desiredLineQuantityRef.current.has(lineId_1)) setLinePending(lineId_1, false);
    });

    // The optimistic value lives as long as this transition: it reverts to
    // the confirmed cart (with the change applied, or restored on failure)
    // once the mutation settles.
    startTransition(async () => {
      applyOptimisticChange({
        type: 'quantity',
        lineId: lineId_1,
        quantity: quantity_0
      });
      await settled;
    });
    return mutation;
  }, [applyOptimisticChange, buildCartRequestHeaders, enqueueLineMutation, getCurrentCartId, persistCartId, recoverCartAfterMutationFailure, setLinePending]);
  const updateLineOptions = useCallback((lineId_2: string, options_1: {
    quantity: number;
    sellingPlanId?: string;
    attributes?: {
      key: string;
      value: string;
    }[];
  }) => {
    // Non-optimistic like updateItem: contents/plan changes reprice the
    // line, so only the server-confirmed cart is rendered.
    if (options_1.quantity < 1 || pendingLineIdsRef.current.has(lineId_2)) {
      return Promise.resolve();
    }
    setLinePending(lineId_2, true);
    return enqueueLineMutation(async () => {
      const mutationVersion_4 = ++cartMutationVersionRef.current;
      try {
        const cartId_5 = getCurrentCartId();
        if (!cartId_5) return;
        const res_2 = await fetch('/api/cart', {
          method: 'POST',
          headers: buildCartRequestHeaders({
            'Content-Type': 'application/json'
          }),
          body: JSON.stringify({
            action: 'update',
            cartId: cartId_5,
            source: 'cart_line_options',
            lines: [{
              id: lineId_2,
              ...options_1
            }]
          })
        });
        const data_0 = await res_2.json();
        if (data_0.cart) {
          persistCartId(data_0.cart.id);
          pendingCartAnalyticsRef.current = {
            source: 'cart_line_options'
          };
          if (cartMutationVersionRef.current === mutationVersion_4) {
            dispatch({
              type: 'SET_CART',
              payload: data_0.cart
            });
          }
        }
      } catch (error_2) {
        console.error('Failed to update cart line options:', error_2);
        await recoverCartAfterMutationFailure();
      } finally {
        setLinePending(lineId_2, false);
      }
    });
  }, [buildCartRequestHeaders, enqueueLineMutation, getCurrentCartId, persistCartId, recoverCartAfterMutationFailure, setLinePending]);
  const convertLineToSubscription = useCallback((lineId_3: string, quantity_1: number, sellingPlanId: string) => {
    // Non-optimistic like updateItem: the plan changes the line's price and
    // triggers the free-shipping code sync, so only the server-confirmed
    // cart is rendered.
    if (quantity_1 < 1 || pendingLineIdsRef.current.has(lineId_3)) return Promise.resolve();
    setLinePending(lineId_3, true);
    return enqueueLineMutation(async () => {
      const mutationVersion_5 = ++cartMutationVersionRef.current;
      try {
        const cartId_6 = getCurrentCartId();
        if (!cartId_6) return;
        const res_3 = await fetch('/api/cart', {
          method: 'POST',
          headers: buildCartRequestHeaders({
            'Content-Type': 'application/json'
          }),
          body: JSON.stringify({
            action: 'update',
            cartId: cartId_6,
            source: 'cart_subscription_upsell',
            lines: [{
              id: lineId_3,
              quantity: quantity_1,
              sellingPlanId
            }]
          })
        });
        const data_1 = await res_3.json();
        if (data_1.cart) {
          persistCartId(data_1.cart.id);
          pendingCartAnalyticsRef.current = {
            source: 'cart_subscription_upsell'
          };
          if (cartMutationVersionRef.current === mutationVersion_5) {
            dispatch({
              type: 'SET_CART',
              payload: data_1.cart
            });
          }
        }
      } catch (error_3) {
        console.error('Failed to upgrade cart line to subscription:', error_3);
        await recoverCartAfterMutationFailure();
      } finally {
        setLinePending(lineId_3, false);
      }
    });
  }, [buildCartRequestHeaders, enqueueLineMutation, getCurrentCartId, persistCartId, recoverCartAfterMutationFailure, setLinePending]);
  const removeItem = useCallback((lineId_4: string) => {
    // Optimistic: the line (and its companion gifts) leaves the drawer on the
    // click; the confirmed cart replaces the approximate totals when the
    // serialized mutation lands, and a failed mutation brings the line back.
    if (pendingLineIdsRef.current.has(lineId_4)) return Promise.resolve();

    // Removing the LAST subscription line takes its free-gift lines along
    // in the SAME mutation — gifts and subscription disappear together
    // (the cleanup effect stays as the safety net for every other path).
    const lineIds_0 = [lineId_4, ...getCompanionGiftLineIds(cartStateRef.current, lineId_4, pendingLineIdsRef.current, giftProductHandles)];
    setLinesPending(lineIds_0, true);
    const mutation_0 = enqueueLineMutation(async () => {
      const mutationVersion_6 = ++cartMutationVersionRef.current;
      try {
        const cartId_7 = getCurrentCartId();
        if (!cartId_7) return;
        const res_4 = await fetch('/api/cart', {
          method: 'POST',
          headers: buildCartRequestHeaders({
            'Content-Type': 'application/json'
          }),
          body: JSON.stringify({
            action: 'remove',
            cartId: cartId_7,
            source: 'cart_remove',
            lineIds: lineIds_0
          })
        });
        const data_2 = await res_4.json();
        if (data_2.cart) {
          persistCartId(data_2.cart.id);
          pendingCartAnalyticsRef.current = {
            source: 'cart_remove'
          };
          if (cartMutationVersionRef.current === mutationVersion_6) {
            dispatch({
              type: 'SET_CART',
              payload: data_2.cart
            });
          }
        }
      } catch (error_4) {
        console.error('Failed to remove cart item:', error_4);
        await recoverCartAfterMutationFailure();
      } finally {
        setLinesPending(lineIds_0, false);
      }
    });
    startTransition(async () => {
      applyOptimisticChange({
        type: 'remove',
        lineIds: lineIds_0
      });
      await mutation_0.catch(() => undefined);
    });
    return mutation_0;
  }, [applyOptimisticChange, buildCartRequestHeaders, enqueueLineMutation, getCurrentCartId, giftProductHandles, persistCartId, recoverCartAfterMutationFailure, setLinesPending]);
  const updateBuyerIdentity = useCallback(async (buyerIdentity: {
    email?: string;
    phone?: string;
    countryCode?: string;
  }) => {
    if (cartMutationInFlightRef.current) return;
    dispatch({
      type: 'SET_LOADING',
      payload: true
    });
    cartMutationInFlightRef.current = true;
    try {
      const cartId_8 = getCurrentCartId();
      if (!cartId_8) return;
      const res_5 = await fetch('/api/cart', {
        method: 'POST',
        headers: buildCartRequestHeaders({
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify({
          action: 'updateBuyerIdentity',
          cartId: cartId_8,
          source: 'buyer_identity_sync',
          buyerIdentity
        })
      });
      const data_3 = await res_5.json();
      if (data_3.cart) {
        persistCartId(data_3.cart.id);
        dispatch({
          type: 'SET_CART',
          payload: data_3.cart
        });

        // Attribute subsequent Klaviyo events to this profile as soon as we have an email.
        if (buyerIdentity.email || buyerIdentity.phone) {
          identifyKlaviyoProfile({
            email: buyerIdentity.email,
            phone: buyerIdentity.phone
          });
          identifyPostHogProfile({
            email: buyerIdentity.email,
            phone: buyerIdentity.phone,
            source: 'cart_buyer_identity'
          });
        }
      }
    } catch (error_5) {
      console.error('Failed to update buyer identity:', error_5);
    } finally {
      cartMutationInFlightRef.current = false;
      dispatch({
        type: 'SET_LOADING',
        payload: false
      });
    }
  }, [buildCartRequestHeaders, getCurrentCartId, persistCartId]);
  useEffect(() => {
    const cart_6 = state.cart;
    if (!cart_6 || cart_6.id === 'optimistic-cart' || !market?.countryCode) return;
    if (cart_6.buyerIdentity?.countryCode === market.countryCode) return;
    void updateBuyerIdentity({
      countryCode: market.countryCode
    });
  }, [market?.countryCode, state.cart, updateBuyerIdentity]);
  useEffect(() => {
    const cart_7 = state.cart;
    if (!cart_7) {
      lastPackageProtectionSignatureRef.current = '';
      return;
    }
    if (Date.now() < packageProtectionSyncSuspendedUntilRef.current) {
      return;
    }
    if (packageProtectionSyncInFlightRef.current) {
      return;
    }
    if (cartMutationInFlightRef.current || pendingLineIdsRef.current.size > 0) {
      return;
    }
    const rawLines_0 = cart_7.lines.edges.map(edge_1 => edge_1.node);
    const signature = buildPackageProtectionSignature(pathname, rawLines_0, packageProtection);
    if (lastPackageProtectionSignatureRef.current === signature) {
      return;
    }
    const {
      visibleLines: visibleLines_0,
      protectionLines
    } = splitPackageProtectionLines(rawLines_0, packageProtection.productHandle);

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: package protection reconciliation handles cleanup, replacement, and cart resync in one guarded flow
    const reconcile = async () => {
      const reconciliationVersion = cartMutationVersionRef.current;
      const canApplyReconciledCart = () => cartMutationVersionRef.current === reconciliationVersion && !cartMutationInFlightRef.current && pendingLineIdsRef.current.size === 0;
      const cartId_9 = getCurrentCartId();
      if (!cartId_9) {
        lastPackageProtectionSignatureRef.current = signature;
        return;
      }
      if (!packageProtection.enabled || pathname !== '/cart') {
        if (protectionLines.length === 0) {
          lastPackageProtectionSignatureRef.current = signature;
          return;
        }
        packageProtectionSyncInFlightRef.current = true;
        try {
          const response_1 = await fetch('/api/cart', {
            method: 'POST',
            headers: buildCartRequestHeaders({
              'Content-Type': 'application/json'
            }),
            body: JSON.stringify({
              action: 'remove',
              cartId: cartId_9,
              source: 'package_protection_cleanup',
              lineIds: protectionLines.map(line_7 => line_7.id)
            })
          });
          const data_4 = await response_1.json();
          if (data_4.cart?.id) {
            persistCartId(data_4.cart.id);
          }
          if (canApplyReconciledCart()) {
            pendingCartAnalyticsRef.current = {
              source: 'package_protection_cleanup'
            };
            dispatch({
              type: 'SET_CART',
              payload: data_4.cart || null
            });
            lastPackageProtectionSignatureRef.current = '';
          }
        } catch (error_6) {
          console.error('Failed to clean up package protection:', error_6);
        } finally {
          packageProtectionSyncInFlightRef.current = false;
        }
        return;
      }
      if (protectionLines.length === 0) {
        lastPackageProtectionSignatureRef.current = signature;
        return;
      }
      const visibleSubtotal = getCartLinesSubtotal(visibleLines_0);
      const selectedVariant = await getSelectedPackageProtectionVariant(packageProtection, visibleSubtotal);
      const alreadyCorrect = selectedVariant && protectionLines.length === 1 && protectionLines[0].merchandise.id === selectedVariant.gid && protectionLines[0].quantity === 1;
      if (alreadyCorrect) {
        lastPackageProtectionSignatureRef.current = signature;
        return;
      }
      packageProtectionSyncInFlightRef.current = true;
      try {
        const removeResponse = await fetch('/api/cart', {
          method: 'POST',
          headers: buildCartRequestHeaders({
            'Content-Type': 'application/json'
          }),
          body: JSON.stringify({
            action: 'remove',
            cartId: cartId_9,
            source: 'package_protection_reconcile',
            lineIds: protectionLines.map(line_8 => line_8.id)
          })
        });
        const removedData = await removeResponse.json();
        let nextCart_1 = removedData.cart || null;
        if (removedData.cart?.id) {
          persistCartId(removedData.cart.id);
        }
        if (canApplyReconciledCart()) {
          pendingCartAnalyticsRef.current = {
            source: 'package_protection_reconcile'
          };
          dispatch({
            type: 'SET_CART',
            payload: nextCart_1
          });
        }
        if (selectedVariant) {
          const addResponse = await fetch('/api/cart', {
            method: 'POST',
            headers: buildCartRequestHeaders({
              'Content-Type': 'application/json'
            }),
            body: JSON.stringify({
              action: 'add',
              cartId: cartId_9,
              source: 'package_protection_reconcile',
              lines: [{
                merchandiseId: selectedVariant.gid,
                quantity: 1
              }]
            })
          });
          const addedData = await addResponse.json();
          nextCart_1 = addedData.cart || nextCart_1;
          if (nextCart_1?.id) {
            persistCartId(nextCart_1.id);
          }
          if (canApplyReconciledCart()) {
            pendingCartAnalyticsRef.current = {
              source: 'package_protection_reconcile'
            };
            dispatch({
              type: 'SET_CART',
              payload: nextCart_1
            });
          }
        }
        lastPackageProtectionSignatureRef.current = '';
      } catch (error_7) {
        console.error('Failed to reconcile package protection:', error_7);
      } finally {
        packageProtectionSyncInFlightRef.current = false;
      }
    };
    void reconcile();
  }, [buildCartRequestHeaders, getCurrentCartId, packageProtection, pathname, persistCartId, state.cart]);
  const cartValue = useMemo(() => ({
    // The UI reads the optimistic cart; internal effects (cookie sync,
    // package-protection reconcile, analytics) keep reading `state.cart`,
    // the server-confirmed one.
    cart: optimisticCart,
    isOpen: state.isOpen,
    isLoading: state.isLoading,
    addItem,
    claimRewardItems,
    updateItem,
    convertLineToSubscription,
    updateLineOptions,
    removeItem,
    updateBuyerIdentity,
    loadCart: loadCartForUi,
    hydrateCart,
    suspendPackageProtectionSync,
    pendingLineIds: state.pendingLineIds
  }), [optimisticCart, state.isLoading, state.isOpen, state.pendingLineIds, addItem, claimRewardItems, updateItem, convertLineToSubscription, updateLineOptions, removeItem, updateBuyerIdentity, loadCartForUi, hydrateCart, suspendPackageProtectionSync]);
  const visibleQuantity = useMemo(() => {
    const rawLines_1 = optimisticCart?.lines.edges.map(edge_2 => edge_2.node) ?? [];
    const {
      visibleLines: visibleLines_1
    } = splitPackageProtectionLines(rawLines_1, packageProtection.productHandle);
    return getCartLinesQuantity(visibleLines_1);
  }, [packageProtection.productHandle, optimisticCart]);
  const cartSummaryValue = useMemo(() => ({
    quantity: optimisticCart ? visibleQuantity : cachedQty,
    isOpen: state.isOpen,
    openCart,
    closeCart
  }), [visibleQuantity, optimisticCart, state.isOpen, cachedQty, openCart, closeCart]);
  return <CartSummaryContext.Provider value={cartSummaryValue}>
			<CartContext.Provider value={cartValue}>{children}</CartContext.Provider>
		</CartSummaryContext.Provider>;
}